'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Trash2, AlertTriangle, ImageOff } from 'lucide-react';
import { ImageDropzone } from '@/components/ui/image-dropzone';
import { uploadImage } from '@/lib/image-upload';

interface EditAlbumDialogProps {
  open: boolean;
  onClose: () => void;
  album: {
    id: string;
    name: string;
    description?: string;
    sortName?: string | null;
    coverUrl?: string | null;
    mediaCount?: number;
  };
  onUpdate?: () => void;
  onDelete?: () => void;
}

export function EditAlbumDialog({
  open,
  onClose,
  album,
  onUpdate,
  onDelete,
}: EditAlbumDialogProps) {
  const firestore = useFirestore();
  const auth = useAuth();
  const { toast } = useToast();

  const [name, setName] = React.useState(album.name);
  const [description, setDescription] = React.useState(album.description || '');
  const [sortName, setSortName] = React.useState(album.sortName || '');
  const [coverUrl, setCoverUrl] = React.useState<string | null>(album.coverUrl || null);
  const [uploadingCover, setUploadingCover] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');

  // Reset form when album changes
  React.useEffect(() => {
    setName(album.name);
    setDescription(album.description || '');
    setSortName(album.sortName || '');
    setCoverUrl(album.coverUrl || null);
    setDeleteConfirmText('');
  }, [album]);

  const handleCoverUpload = async (file: File) => {
    setUploadingCover(true);
    try {
      const url = await uploadImage(file, { category: 'site', subdir: 'album-covers' });
      setCoverUrl(url);
    } catch (error) {
      console.error('Error uploading album cover:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Could not upload cover image.',
      });
      throw error;
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveCover = () => {
    setCoverUrl(null);
  };

  const handleSave = async () => {
    if (!firestore || !name.trim()) return;

    setSaving(true);
    try {
      const albumRef = doc(firestore, 'albums', album.id);
      await updateDoc(albumRef, {
        name: name.trim(),
        description: description.trim(),
        sortName: sortName.trim() || null,
        coverUrl: coverUrl || null,
        updatedAt: Timestamp.now(),
      });

      // Invalidate hierarchy cache so sidebar updates
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : undefined;
      fetch('/api/gallery/sync-hierarchy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ role: token ? 'members' : 'public' }),
      }).catch(err => console.warn('Failed to sync hierarchy cache:', err));

      toast({
        title: 'Album updated',
        description: 'Album details have been saved.',
      });

      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error updating album:', error);
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Could not update album.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!auth.currentUser) return;

    setDeleting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('/api/gallery/delete-album', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ albumId: album.id }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete album');
      }

      toast({
        title: 'Album deleted',
        description: 'The album and its contents have been removed.',
      });

      setShowDeleteConfirm(false);
      onDelete?.();
      onClose();
    } catch (error) {
      console.error('Error deleting album:', error);
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Could not delete album.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = deleteConfirmText.toLowerCase() === 'delete';

  return (
    <>
      <Dialog open={open && !showDeleteConfirm} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-md max-h-[75vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Album</DialogTitle>
            <DialogDescription>
              Update album details or delete the album.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto flex-1 -mx-6 px-6">
            <div className="space-y-2">
              <Label htmlFor="album-name">Name</Label>
              <Input
                id="album-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Album name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="album-description">Description</Label>
              <Textarea
                id="album-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                className="h-24 resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="album-sort-name">Sort Name</Label>
              <Input
                id="album-sort-name"
                value={sortName}
                onChange={(e) => setSortName(e.target.value)}
                placeholder={name || 'Album name'}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Used for custom sorting order. Leave empty to sort by album name.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Cover Image</Label>
              <p className="text-xs text-muted-foreground">
                Optional. Shown on the album card. If not set, the folder icon is displayed.
              </p>
              <div className="aspect-video w-full max-w-sm">
                <ImageDropzone
                  currentImageUrl={coverUrl || undefined}
                  onUpload={handleCoverUpload}
                  uploading={uploadingCover}
                  shape="square"
                  maxWidth={1600}
                  maxHeight={1600}
                />
              </div>
              {coverUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveCover}
                  disabled={uploadingCover || saving}
                >
                  <ImageOff className="w-4 h-4 mr-2" />
                  Remove cover image
                </Button>
              )}
            </div>

            {/* Danger Zone */}
            <div className="pt-4 border-t">
              <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-destructive">Danger Zone</h4>
                    <p className="text-xs text-muted-foreground mt-1 mb-3">
                      Deleting this album will permanently remove it and all {album.mediaCount || 0} media items inside.
                      This action cannot be undone.
                    </p>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Album
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Album Permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to delete <strong>"{album.name}"</strong> and all of its contents.
              </p>
              {(album.mediaCount ?? 0) > 0 && (
                <p className="text-destructive font-medium">
                  This will permanently delete {album.mediaCount} media item(s).
                </p>
              )}
              <p>
                Type <strong>delete</strong> below to confirm:
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                className="mt-2"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} onClick={() => setDeleteConfirmText('')}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!canDelete || deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Forever
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
